import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';
import DatadogUserTagger from '../components/containers/datadog-user-tagger/datadog-user-tagger';
import Gamification from './services/Gamification';
import Header from './components/Header';
import OTP from './pages/OTP';
import LogOut from './pages/LogOut';
import AchievementDrawer from './components/AchievementDrawer';
import SplitIOTreatment from '../components/splitio/splitioTreatment';
import { filterByRole } from './utils/claimsMapper';
import getChampion from './services/Champion';
import GlobalStyles from './components/GlobalStyles';
import PageFooter from './components/PageFooter';
import { routes } from './routes';
import { PLACEMENT } from './types';
import { setMainNav, setDropdownNav } from '../modules/navigation';
import { getTreatmentAsync } from '../modules/splitIO_api';

import { datadogRum } from '@datadog/browser-rum';
import splitsDictionary from '../services/utils/splitsDictionary';
import ResetPassword from './pages/ResetPassword';
import PasswordSuccess from './pages/PasswordSuccess';
import TournamentScaffold from './components/TournamentScaffold';
import ActivityAttribution from './components/ActivityAttribution';
import { setInitialPlatform, PLATFORMS } from './utils/setInitialPlatform';

const Main = styled.main`
  padding: 48px 35px 32px;
  position: relative;
  z-index: 1;

  &.topIndex {
    position: initial;
  }

  @media print {
    padding: 48px 0 32px;
  }
`;

document.querySelector('body').classList.add('newUI');

const Scaffold = () => {
  const [champion, setChampion] = useState(null);
  const [achievementDrawerOpen, setAchievementDrawerOpen] = useState(false);
  const [notificationDotStatus, setNotificationDotStatus] = useState(false);
  const [filteredRoutes, setFilteredRoutes] = useState([]);

  const [platform, setPlatform] = useState('');

  const {
    isMasquaraded,
    roleLevel,
    currentUserId,
    requirePasswordChange: resetPassword,
    otpRequired,
    realm_name,
  } = useSelector((state) => state.user, shallowEqual);

  const keycloakTreatment = useSelector((state) => state.splitIO.treatments['keycloak-auth']);
  const forceResetPassword = resetPassword && (!realm_name || keycloakTreatment !== 'on');
  const dispatch = useDispatch();
  const history = useHistory();

  const handleHistory = () => {
    window.prevUrl = window.currentUrl;
    window.currentUrl = window.location.href;
  };

  useEffect(() => {
    window.prevUrl = '';
    window.currentUrl = window.location.href;
    return history.listen(handleHistory);
  }, [history]);

  useEffect(() => {
    Gamification.getNotificationDotStatus().then((response) => {
      setNotificationDotStatus(response);
    });

    getChampion().then((data) => {
      setChampion(data);
    });
  }, []);

  useEffect(async () => {
    // get routes by role based on claims
    const relevantRoutes = filterByRole(routes(currentUserId), roleLevel, isMasquaraded);

    // Temporary method to set platform based on role
    setPlatform(setInitialPlatform(roleLevel));

    // reduce splits down to a unique list
    const splits = relevantRoutes
      .filter((link) => 'featureToggle' in link)
      .reduce((target, link) => {
        target[link.featureToggle.name] = link.featureToggle;
        return target;
      }, {});

    // get treatments for each split
    const _treatments = await Promise.all(
      Object.keys(splits).map(async (key) => {
        const treatment = await getTreatmentAsync(key, splits[key].type);
        return { key: key, treatment: treatment.data.treatment[key] };
      })
    );

    const treatments = _treatments.reduce((target, treatment) => {
      target[treatment.key] = treatment.treatment;
      return target;
    }, {});

    // filter links based on treatments
    const toggledLinks = relevantRoutes.filter((link) => {
      const treatmentValue = link.featureToggle?.treatmentValue || false;

      if (!('featureToggle' in link)) {
        return true;
      }

      if (!treatmentValue && treatments[link.featureToggle.name] === 'on') {
        return true;
      }

      if (treatmentValue === treatments[link.featureToggle.name]) {
        return true;
      }

      return false;
    });

    const dropDownLinks = toggledLinks.filter((link) => link.placement === PLACEMENT.DROPDOWN);
    const mainLinks = toggledLinks.filter((link) => link.placement === PLACEMENT.NAVBAR);

    dispatch(setMainNav(mainLinks));
    dispatch(setDropdownNav(dropDownLinks));
    setFilteredRoutes(toggledLinks);
  }, [roleLevel, currentUserId]);

  return (
    <>
      <GlobalStyles />
      <DatadogUserTagger />
      {otpRequired && (
        <>
          <Route exact path="/logout" component={LogOut} />
          <Route render={() => <OTP />} />
        </>
      )}

      {!otpRequired && (
        <>
          <TournamentScaffold />
          <ActivityAttribution />
          <Header
            notificationDotStatus={notificationDotStatus}
            openAchievementDrawer={() => {
              datadogRum.addAction('myRewardsClicked', {});
              setAchievementDrawerOpen(true);
              setNotificationDotStatus({ on: false });
            }}
          />

          <Main>
            <Switch>
              <Route component={ResetPassword} path="/reset-password" exact />
              <Route component={PasswordSuccess} path="/password-success" exact />
              {forceResetPassword && <Route render={() => <Redirect to="/reset-password" />} />}

              {filteredRoutes.map((componentData, i) => {
                const { component, render, children, path, exact, strict, location, sensitive } = componentData;

                return (
                  <Route
                    key={i}
                    component={component}
                    render={render}
                    children={children}
                    path={path}
                    exact={exact}
                    strict={strict}
                    location={location}
                    sensitive={sensitive}
                  />
                );
              })}

              {
                // Get filtered routes before redirecting to dashboard
                filteredRoutes.length && !forceResetPassword && (
                  <Route exact path="/" render={() => <Redirect to="/dashboard" />} />
                )
              }
            </Switch>
          </Main>

          <Switch>
            {filteredRoutes.map((componentData, i) => {
              const { path, exact, strict, hideFooter } = componentData;
              return hideFooter ? (
                <Route key={`hideFooter-${i}`} path={path} strict={strict} exact={exact}></Route>
              ) : null;
            })}

            <Route>
              <PageFooter champion={champion} isTherapy={platform === PLATFORMS.THERAPY} />
            </Route>
          </Switch>

          <SplitIOTreatment
            featureName={splitsDictionary.GAMIFICATION.name}
            treatmentValue="on"
            trafficType={splitsDictionary.GAMIFICATION.traffic_type}
          >
            {(treatment) => {
              if (treatment === 'on') {
                return (
                  <AchievementDrawer
                    open={achievementDrawerOpen}
                    closeCallback={() => {
                      setAchievementDrawerOpen(false);
                    }}
                  ></AchievementDrawer>
                );
              } else {
                return null;
              }
            }}
          </SplitIOTreatment>
        </>
      )}
    </>
  );
};

export default Scaffold;
